<template>
	<div class="tabs">
		<div
			:class="[
				'overflow-scroll overflow-scroll-no-wrap display-flex justify-content-space-between align-items-center width-100',
				{ bb: props.setBorder }
			]"
			data-test="tabs-list-wrapper"
		>
			<div class="tab-list" role="tablist">
				<button
					v-for="(tab, index) in props.tabs"
					:id="`tab-${tab.key}`"
					:key="`tab-${tab.key}`"
					ref="tabsRef"
					role="tab"
					:tabindex="isActive(index) ? 0 : -1"
					:aria-controls="hasPanels ? `tab-panel-${tab.key}` : ''"
					:aria-selected="isActive(index)"
					:class="['tab', { 'tab-active': isActive(index) }]"
					:data-test="`tab-${tab.key}`"
					:disabled="isActive(index)"
					@click="handleSelectedTab(tab.key, index)"
					@keydown="handleKeyboardNav($event, index)"
				>
					{{ tab.label }}
				</button>
			</div>
			<slot name="tabs-header"></slot>
		</div>
		<div
			v-if="hasPanels"
			class="tabbed-panels-content mt-150"
			:class="`tab-panel-content--${props.tabs[activeTabIndex].key}`"
		>
			<transition name="component-fade" mode="out-in">
				<keep-alive v-if="props.keepAlive">
					<component
						:is="tabPanels[activeTabIndex].component"
						v-bind="tabPanels[activeTabIndex].props"
						:id="`tab-panel-${props.tabs[activeTabIndex].key}`"
						role="tabpanel"
						tabindex="0"
						:aria-labelledby="`tab-${props.tabs[activeTabIndex].key}`"
					/>
				</keep-alive>
				<component
					:is="tabPanels[activeTabIndex].component"
					v-else
					v-bind="tabPanels[activeTabIndex].props"
					:id="`tab-panel-${tabs[activeTabIndex].key}`"
					role="tabpanel"
					tabindex="0"
					:aria-labelledby="`tab-${props.tabs[activeTabIndex].key}`"
				/>
			</transition>
		</div>
	</div>
</template>
<script setup lang="ts">
import { computed, markRaw, nextTick, ref, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DynamicComponent } from 'types/vue';
import { scrollToAnchor } from '@utils/scroll-to';
import { Tab } from 'types/layout';
import { useMixpanel } from '@utils/composables/use-mixpanel';

interface Props {
	tabs: Array<Tab>;
	defaultTabIndex?: number;
	setBorder?: boolean;
	passSelectionToRoute?: boolean;
	keepAlive?: boolean;
}

interface Emits {
	(e: 'active-tab', value: Tab): void;
}

const props = withDefaults(defineProps<Props>(), {
	defaultTabIndex: 0,
	setBorder: false,
	passSelectionToRoute: false,
	keepAlive: false
});

const emits = defineEmits<Emits>();

const route = useRoute();
const router = useRouter();

const tabsRef: Ref<Array<HTMLElement>> = ref([]);

const activeTabIndex = ref(props.defaultTabIndex);

const { trackMixpanelClick } = useMixpanel();

const tabPanels = computed((): Array<DynamicComponent> => {
	const panels: Array<DynamicComponent> = [];

	props.tabs.forEach((tab) => {
		if (tab.panel) {
			panels.push(markRaw(tab.panel));
		}
	});
	return panels;
});

const hasPanels = computed((): boolean => tabPanels.value.length > 0);
const routeHash = computed((): string => route?.hash?.split('#')[1] || '');

function isActive(index: number): boolean {
	return activeTabIndex.value === index;
}

function handleSelectedTab(anchor: string, index: number): void {
	activeTabIndex.value = index;

	if (!hasPanels.value) {
		scrollToAnchor(anchor);
	}

	const activeTab = props.tabs[activeTabIndex.value];
	trackMixpanelClick({
		'Action Element': 'Tab',
		'Action Target': activeTab.label,
		'Action Context': 'Tab Navigation'
	});

	if (routeHash.value !== anchor && props.passSelectionToRoute) {
		nextTick(() => router.replace({ hash: `#${anchor}` }));
	}

	emits('active-tab', activeTab);
}

function handleKeyboardNav(e: KeyboardEvent, focusedTabbedIndex: number): void {
	const tabsCount = props.tabs.length - 1;

	switch (e.key) {
		case 'ArrowRight':
			if (focusedTabbedIndex < tabsCount) {
				focusedTabbedIndex += 1;
			} else {
				focusedTabbedIndex = 0;
			}
			break;
		case 'ArrowLeft':
			if (focusedTabbedIndex === 0) {
				focusedTabbedIndex = tabsCount;
			} else {
				focusedTabbedIndex -= 1;
			}
			break;
		case 'Enter': {
			const key = tabsRef.value[focusedTabbedIndex].id;
			handleSelectedTab(key, focusedTabbedIndex);
			break;
		}
		default: {
			break;
		}
	}

	tabsRef.value[focusedTabbedIndex].focus();
}

function setup() {
	if (routeHash.value) {
		const persistedTab = props.tabs.find((tab) => {
			return tab.key === routeHash.value;
		});

		if (persistedTab) {
			const persistedTabIndex = props.tabs.indexOf(persistedTab);
			nextTick(() => handleSelectedTab(persistedTab.key, persistedTabIndex));
		}
	} else if (props.passSelectionToRoute) {
		const defaultTabKey = props.tabs[activeTabIndex.value].key;
		router.replace({ hash: `#${defaultTabKey}` });
	}
}

setup();
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors' as *;
@use '../../styles/constants/typography' as *;

.tab-list {
	color: token('content-secondary');
	display: flex;
	font-family: $base-font-family;
	flex-direction: row;
	justify-content: left;
	align-items: flex-start;
	gap: 2rem;

	.tab {
		background: transparent;
		border: none;
		border-radius: 0;
		font-size: 18px;
		font-weight: 600;
		line-height: 1.5;
		padding: 0 4px 8px;
		position: relative;

		&::before {
			background: token('content-primary');
			bottom: 0;
			content: '';
			height: 2px;
			left: 0;
			opacity: 0;
			position: absolute;
			transition: all 300ms ease;
			width: 100%;
		}

		&-active {
			color: token('content-primary');
		}

		&-active,
		&:hover {
			&::before {
				opacity: 1;
			}
		}

		&:focus-visible {
			color: token('content-primary');
			outline: -webkit-focus-ring-color auto 1px;
		}
	}
}

.component-fade-enter-active,
.component-fade-leave-active {
	transition: all 0.3s ease;
}

.component-fade-enter,
.component-fade-leave-to {
	opacity: 0;
}
</style>
